/**
 * Quiet hours / per-airport flight curfew.
 *
 * Extend by adding an ICAO row to AIRPORT_HOURS. Unknown fields use
 * DEFAULT_AIRPORT_HOURS (America/Halifax, 23:00–06:00 local). Set
 * unrestricted: true for 24-hour pads. Times are local wall clocks in the
 * row's IANA zone — never invent ops times in the Grok brain.
 * That Halifax default is quiet hours only. Desk Local clocks use
 * timeZoneForIcao and fail closed when an ICAO has no zone.
 */
import { minutesOfDayInTimeZone, partsInTimeZone, wallTimeInTimeZone } from "./clock";

export type AirportHours = {
  icao: string;
  timeZone: string;
  /** Local minutes from midnight when quiet / no-flight starts (e.g. 22:00 → 1320). */
  quietStartMin: number;
  /** Local minutes from midnight when quiet ends (e.g. 07:00 → 420). */
  quietEndMin: number;
  /** 24-hour field — no quiet window. */
  unrestricted?: boolean;
  note?: string;
};

export const DEFAULT_AIRPORT_HOURS: Omit<AirportHours, "icao"> = {
  timeZone: "America/Halifax",
  quietStartMin: 23 * 60,
  quietEndMin: 6 * 60,
  note: "Default night quiet hours for unknown ICAOs. Add a row in AIRPORT_HOURS to override.",
};

/** Published / desk-training quiet hours. Not a legal AIP reprint. */
export const AIRPORT_HOURS: AirportHours[] = [
  {
    icao: "CYPD",
    timeZone: "America/Halifax",
    quietStartMin: 22 * 60,
    quietEndMin: 7 * 60,
    note: "Port Hawkesbury — small uncontrolled field; night quiet 22:00–07:00 ADT/AST.",
  },
  {
    icao: "CYHZ",
    timeZone: "America/Halifax",
    quietStartMin: 0,
    quietEndMin: 0,
    unrestricted: true,
    note: "Halifax Stanfield — 24-hour pad (winter base). No hard curfew on this desk.",
  },
  {
    icao: "KBEC",
    timeZone: "America/Chicago",
    quietStartMin: 22 * 60,
    quietEndMin: 6 * 60,
    note: "Beech Factory — factory-field quiet 22:00–06:00 CT.",
  },
  {
    icao: "CYQY",
    timeZone: "America/Halifax",
    quietStartMin: 22 * 60,
    quietEndMin: 7 * 60,
  },
  {
    icao: "CYYT",
    timeZone: "America/St_Johns",
    quietStartMin: 23 * 60,
    quietEndMin: 6 * 60,
  },
  {
    icao: "CYQM",
    timeZone: "America/Halifax",
    quietStartMin: 23 * 60,
    quietEndMin: 6 * 60,
  },
  {
    icao: "CYYG",
    timeZone: "America/Halifax",
    quietStartMin: 22 * 60,
    quietEndMin: 7 * 60,
  },
  {
    icao: "CYQX",
    timeZone: "America/St_Johns",
    quietStartMin: 23 * 60,
    quietEndMin: 6 * 60,
  },
  {
    icao: "CYYZ",
    timeZone: "America/Toronto",
    quietStartMin: 0 * 60 + 30,
    quietEndMin: 6 * 60 + 30,
    note: "Toronto Pearson restricted hours 00:30–06:30 ET (desk model).",
  },
  {
    icao: "CYUL",
    timeZone: "America/Toronto",
    quietStartMin: 1 * 60,
    quietEndMin: 7 * 60,
    note: "Montréal-Trudeau night restriction 01:00–07:00 ET (desk model).",
  },
  {
    icao: "CYOW",
    timeZone: "America/Toronto",
    quietStartMin: 23 * 60,
    quietEndMin: 6 * 60,
  },
  {
    icao: "CYQB",
    timeZone: "America/Toronto",
    quietStartMin: 23 * 60,
    quietEndMin: 6 * 60,
  },
  {
    icao: "KBOS",
    timeZone: "America/New_York",
    quietStartMin: 23 * 60,
    quietEndMin: 6 * 60,
    note: "Logan noise abatement 23:00–06:00 ET.",
  },
  {
    icao: "KTEB",
    timeZone: "America/New_York",
    quietStartMin: 23 * 60,
    quietEndMin: 6 * 60,
  },
  {
    icao: "KHPN",
    timeZone: "America/New_York",
    quietStartMin: 23 * 60,
    quietEndMin: 6 * 30,
  },
  {
    icao: "KJFK",
    timeZone: "America/New_York",
    quietStartMin: 0,
    quietEndMin: 6 * 60,
  },
  {
    icao: "KEWR",
    timeZone: "America/New_York",
    quietStartMin: 0,
    quietEndMin: 6 * 60,
  },
  {
    icao: "KDCA",
    timeZone: "America/New_York",
    quietStartMin: 22 * 60,
    quietEndMin: 7 * 60,
  },
  {
    icao: "KACK",
    timeZone: "America/New_York",
    quietStartMin: 22 * 60,
    quietEndMin: 7 * 60,
  },
  {
    icao: "KICT",
    timeZone: "America/Chicago",
    quietStartMin: 0,
    quietEndMin: 0,
    unrestricted: true,
  },
];

const HOURS_BY_ICAO = new Map(AIRPORT_HOURS.map((row) => [row.icao, row]));

export function icaoKey(icao: string | null | undefined): string {
  return (icao ?? "").trim().toUpperCase();
}

export function hoursForAirport(icao: string | null | undefined): AirportHours {
  const key = icaoKey(icao);
  const row = key ? HOURS_BY_ICAO.get(key) : undefined;
  if (row) return row;
  return { icao: key || "UNKNOWN", ...DEFAULT_AIRPORT_HOURS };
}

/** True when the instant falls in that field's quiet / curfew window. */
export function isInQuietHours(icao: string | null | undefined, at: Date, hours: AirportHours = hoursForAirport(icao)): boolean {
  if (hours.unrestricted) return false;
  const minutes = minutesOfDayInTimeZone(at, hours.timeZone);
  const { quietStartMin, quietEndMin } = hours;
  if (quietStartMin === quietEndMin) return false;
  if (quietStartMin < quietEndMin) {
    return minutes >= quietStartMin && minutes < quietEndMin;
  }
  return minutes >= quietStartMin || minutes < quietEndMin;
}

function addLocalDays(hours: AirportHours, year: number, month: number, day: number, delta: number): {
  year: number;
  month: number;
  day: number;
} {
  const noon = wallTimeInTimeZone(hours.timeZone, year, month, day, 12, 0);
  noon.setTime(noon.getTime() + delta * 24 * 60 * 60_000);
  const p = partsInTimeZone(noon, hours.timeZone);
  return { year: Number(p.year), month: Number(p.month), day: Number(p.day) };
}

function wallAt(hours: AirportHours, y: number, mo: number, d: number, minutes: number): Date {
  const clamped = ((minutes % (24 * 60)) + 24 * 60) % (24 * 60);
  return wallTimeInTimeZone(hours.timeZone, y, mo, d, Math.floor(clamped / 60), clamped % 60, 0);
}

/** Last instant at or before `at` that is outside quiet hours (1-minute grain). */
export function lastLegalInstant(icao: string | null | undefined, at: Date): Date {
  const hours = hoursForAirport(icao);
  if (hours.unrestricted || !isInQuietHours(icao, at)) return at;
  const p = partsInTimeZone(at, hours.timeZone);
  const y = Number(p.year);
  const mo = Number(p.month);
  const d = Number(p.day);
  const minutes = Number(p.hour) * 60 + Number(p.minute);
  if (hours.quietStartMin > hours.quietEndMin) {
    if (minutes >= hours.quietStartMin) {
      return wallAt(hours, y, mo, d, hours.quietStartMin - 1);
    }
    const prev = addLocalDays(hours, y, mo, d, -1);
    return wallAt(hours, prev.year, prev.month, prev.day, hours.quietStartMin - 1);
  }
  if (minutes < hours.quietEndMin) {
    const prev = addLocalDays(hours, y, mo, d, -1);
    return wallAt(hours, prev.year, prev.month, prev.day, hours.quietStartMin - 1);
  }
  return wallAt(hours, y, mo, d, hours.quietStartMin - 1);
}

/** Next instant at or after `at` that is outside quiet hours. */
export function nextLegalInstant(icao: string | null | undefined, at: Date): Date {
  const hours = hoursForAirport(icao);
  if (hours.unrestricted || !isInQuietHours(icao, at)) return at;
  const p = partsInTimeZone(at, hours.timeZone);
  const y = Number(p.year);
  const mo = Number(p.month);
  const d = Number(p.day);
  const minutes = Number(p.hour) * 60 + Number(p.minute);
  if (hours.quietStartMin > hours.quietEndMin) {
    if (minutes < hours.quietEndMin) {
      return wallAt(hours, y, mo, d, hours.quietEndMin);
    }
    const next = addLocalDays(hours, y, mo, d, 1);
    return wallAt(hours, next.year, next.month, next.day, hours.quietEndMin);
  }
  if (minutes >= hours.quietStartMin) {
    const next = addLocalDays(hours, y, mo, d, 1);
    return wallAt(hours, next.year, next.month, next.day, hours.quietEndMin);
  }
  return wallAt(hours, y, mo, d, hours.quietEndMin);
}

export function formatQuietWindow(hours: AirportHours): string {
  if (hours.unrestricted) return "unrestricted (24h)";
  const fmt = (min: number) => `${String(Math.floor(min / 60)).padStart(2, "0")}:${String(min % 60).padStart(2, "0")}`;
  return `${fmt(hours.quietStartMin)}–${fmt(hours.quietEndMin)} ${hours.timeZone}`;
}

export type FerryCurfewCheck = {
  legal: boolean;
  depQuiet: boolean;
  arrQuiet: boolean;
  fromIcao: string;
  toIcao: string;
};

export function ferryTimesLegal(
  fromIcao: string,
  toIcao: string,
  departure: Date,
  eteMin: number,
  hoursFor: (icao: string | null | undefined) => AirportHours = hoursForAirport,
): FerryCurfewCheck {
  const arrival = new Date(departure.getTime() + Math.max(0, eteMin) * 60_000);
  const depQuiet = isInQuietHours(fromIcao, departure, hoursFor(fromIcao));
  const arrQuiet = isInQuietHours(toIcao, arrival, hoursFor(toIcao));
  return {
    legal: !depQuiet && !arrQuiet,
    depQuiet,
    arrQuiet,
    fromIcao: icaoKey(fromIcao),
    toIcao: icaoKey(toIcao),
  };
}

export function curfewClashMessage(
  check: FerryCurfewCheck,
  hoursFor: (icao: string | null | undefined) => AirportHours = hoursForAirport,
): string {
  const bits: string[] = [];
  if (check.depQuiet) bits.push(`dep ${check.fromIcao} (${formatQuietWindow(hoursFor(check.fromIcao))})`);
  if (check.arrQuiet) bits.push(`arr ${check.toIcao} (${formatQuietWindow(hoursFor(check.toIcao))})`);
  return bits.length
    ? `CURFEW  ferry ${check.fromIcao}–${check.toIcao} overlaps quiet hours (${bits.join("; ")}). No legal window before charter STD — move STD or accept the clash.`
    : "";
}
